import React, { useMemo } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { transformTableData } from 'utils';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import { getEventsList } from 'http/events';
import getTableConfig from './helpers/getTableConfig';

const SOURCE_ENTITY_OPTIONS = [
  'User',
  'Job Role',
  'Job Role Version',
  'Training Module',
  'Training Module Version',
  'Training Phase',
  'Training Session',
  'Evidence',
  'Company',
  'Station',
  'Platform',
  'Notification',
  'Compliance Engine',
  'Regulatory Engine',
];

const EVENT_TYPE_OPTIONS = ['Business Event', 'System Event'];

const Events = ({ tableData }) => {
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const showCompanyColumn = role === ROLES.HYPER;
  const tableConfig = useMemo(() => getTableConfig({ showCompanyColumn }), [showCompanyColumn, t]);
  const [columns, events] = transformTableData(tableConfig, tableData.data);
  const isLoading = tableData.isFetchingTableData;

  return (
    <PageLayout pageTitle={t('events.pageTitle')}>
      <TableFilterToolbar
        filters={[
          {
            type: 'singleSelect',
            name: 'sourceEntity',
            label: t('tablesLabels.sourceEntity'),
            options: SOURCE_ENTITY_OPTIONS.map(value => ({
              value,
              label: value,
            })),
          },
          {
            type: 'singleSelect',
            name: 'eventType',
            label: t('tablesLabels.eventType'),
            options: EVENT_TYPE_OPTIONS.map(value => ({
              value,
              label: value,
            })),
          },
          {
            type: 'search',
            name: 'search',
            label: t('tablesLabels.eventName'),
            placeholder: t('events.searchPlaceholder'),
          },
        ]}
        filtering={tableData.filtering}
        onFilterChange={tableData.handleFiltering}
        onRemoveFilter={tableData.handleRemoveFilter}
        onClearFilters={tableData.handleClearFilters}
      />
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={events}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

Events.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable(
  {
    allowedFilters: ['sourceEntity', 'eventType', 'search'],
    defaultSorting: {
      field: 'occurredAt',
      dir: 'desc',
    },
    sortingFieldsMapping: {
      eventName: 'eventNameSort',
      eventType: 'eventTypeSort',
    },
    fetchingData: 'items',
  },
  getEventsList,
)(Events);
